import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  approveAiCheckpoint,
  getAiCheckpoint,
  modifyAiCheckpoint,
  rejectAiCheckpoint,
  revertAiCheckpoint,
  useAiPageGeneration,
  type AiCheckpointDetail,
  type PuckComponent,
  type PuckData,
} from "@/hooks/use-page-ai";
import type { PuckDiffOp } from "@ease/page-builder";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

interface VersionEntry {
  versionNumber: number;
  description: string;
  timestamp: number;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function buildDraftTitle(pageType: string) {
  const normalized = pageType.trim();
  if (!normalized) {
    return "AI Draft";
  }
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)} Draft`;
}

function getComponentId(component: PuckComponent, index: number) {
  const propsId = component.props?.id;
  return typeof propsId === "string" && propsId.length > 0 ? propsId : `${component.type}-${index}`;
}

function describeDiff(diff: PuckDiffOp) {
  switch (diff.action) {
    case "add_component":
      return `Add ${diff.component.type} at ${diff.position}`;
    case "remove_component":
      return `Remove ${diff.componentId}`;
    case "move_component":
      return `Move ${diff.componentId} to ${diff.newPosition}`;
    case "update_props":
      return `Update ${diff.componentId}: ${Object.keys(diff.props).join(", ") || "props"}`;
    default:
      return "Update page";
  }
}

function ChatPanel({ messages, onSend, isLoading }: {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  isLoading: boolean;
}) {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) {
      return;
    }
    onSend(trimmed);
    setInput("");
  }, [input, isLoading, onSend]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div className="flex h-full flex-col border-r border-[var(--color-border)]">
      <div className="border-b border-[var(--color-border)] p-4">
        <h2 className="text-lg font-semibold">Conversational Builder</h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Every prompt creates or refines a real AI checkpoint version before approval.
        </p>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="mt-8 text-center text-[var(--color-muted-foreground)]">
            <p className="text-lg">Describe the page you want to build.</p>
            <p className="mt-2 text-sm">Try: &quot;Create a premium landing page for a yoga studio&quot;</p>
            <p className="mt-2 text-xs">After the draft appears, click a section and say &quot;make it bigger&quot;.</p>
          </div>
        ) : null}

        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-4 py-2 ${
              message.role === "user"
                ? "bg-[var(--color-primary)] text-white"
                : "bg-[var(--color-muted)] text-[var(--color-foreground)]"
            }`}>
              <p className="whitespace-pre-wrap text-sm">{message.content}</p>
            </div>
          </div>
        ))}

        {isLoading ? (
          <div className="flex justify-start">
            <div className="rounded-lg bg-[var(--color-muted)] px-4 py-2">
              <div className="flex space-x-1">
                <div className="h-2 w-2 animate-bounce rounded-full bg-[var(--color-muted-foreground)]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-[var(--color-muted-foreground)]" style={{ animationDelay: "0.1s" }} />
                <div className="h-2 w-2 animate-bounce rounded-full bg-[var(--color-muted-foreground)]" style={{ animationDelay: "0.2s" }} />
              </div>
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-[var(--color-border)] p-4">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the next change..."
            className="flex-1 resize-none rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            rows={2}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function ComponentPreview({ component, index, selected, onSelect }: {
  component: PuckComponent;
  index: number;
  selected: boolean;
  onSelect: (componentId: string) => void;
}) {
  const props = component.props ?? {};
  const isLoading = props._loading === true;
  const isSkeleton = props._skeleton === true;
  const componentId = getComponentId(component, index);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(componentId)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(componentId);
        }
      }}
      className={`rounded-lg border p-4 text-left transition-all duration-300 ${
        selected ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 ring-2 ring-[var(--color-primary)]/25" :
        isLoading ? "animate-pulse border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5" :
        isSkeleton ? "border-dashed border-[var(--color-border)] bg-[var(--color-surface)]" :
        "border-[var(--color-border)] bg-background hover:border-[var(--color-primary)]/60"
      }`}
      data-component-id={componentId}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-mono text-[var(--color-muted-foreground)]">{component.type}</span>
        <span className={`text-xs ${selected ? "font-semibold text-[var(--color-primary)]" : "text-[var(--color-muted-foreground)]"}`}>
          {selected ? "Selected" : componentId}
        </span>
      </div>

      {isSkeleton ? (
        <div className="space-y-2">
          <div className="h-4 w-3/4 rounded bg-[var(--color-muted)]" />
          <div className="h-3 w-1/2 rounded bg-[var(--color-muted)]" />
        </div>
      ) : (
        <div className="text-sm text-[var(--color-foreground)]">
          {typeof props.heading === "string" ? <h3 className="font-semibold">{props.heading}</h3> : null}
          {typeof props.subheading === "string" ? <p className="text-[var(--color-muted-foreground)]">{props.subheading}</p> : null}
          {typeof props.buttonText === "string" ? (
            <button className="mt-2 rounded bg-[var(--color-primary)] px-3 py-1 text-xs text-white">
              {props.buttonText}
            </button>
          ) : null}
          {typeof props.heading !== "string" && typeof props.buttonText !== "string" ? (
            <p className="italic text-[var(--color-muted-foreground)]">
              {Object.keys(props).filter((key) => !key.startsWith("_")).length} props configured
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PreviewPanel({ state, isLoading, versionNumber, selectedComponentId, onSelectComponent }: {
  state: PuckData;
  isLoading: boolean;
  versionNumber: number | null;
  selectedComponentId: string | null;
  onSelectComponent: (componentId: string) => void;
}) {
  const content = state.content ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] p-3">
        <div>
          <h2 className="text-sm font-medium text-[var(--color-muted-foreground)]">Checkpoint Preview</h2>
          {versionNumber !== null ? (
            <p className="text-xs text-[var(--color-muted-foreground)]">Viewing version v{versionNumber}</p>
          ) : null}
        </div>
        {isLoading ? (
          <span className="animate-pulse text-xs text-[var(--color-primary)]">Updating checkpoint…</span>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto bg-background">
        {content.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[var(--color-muted-foreground)]">
            <p>Your checkpoint preview will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {content.map((component, index) => (
              <ComponentPreview
                key={`${component.type}-${index}`}
                component={component}
                index={index}
                selected={getComponentId(component, index) === selectedComponentId}
                onSelect={onSelectComponent}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DiffPreviewPanel({ diffs, selectedComponentId }: {
  diffs: PuckDiffOp[];
  selectedComponentId: string | null;
}) {
  if (diffs.length === 0 && !selectedComponentId) {
    return null;
  }

  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">Diff-native editing</span>
        {selectedComponentId ? (
          <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-primary)]">
            selected: {selectedComponentId}
          </span>
        ) : (
          <span className="text-xs text-[var(--color-muted-foreground)]">
            Click a section, then say "make it bigger" or "rewrite this CTA".
          </span>
        )}
      </div>
      {diffs.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {diffs.map((diff, index) => (
            <li
              key={`${diff.action}-${index}`}
              className="rounded border border-[var(--color-border)] bg-background px-2 py-1 text-xs text-[var(--color-muted-foreground)]"
            >
              {describeDiff(diff)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function VersionBar({ versions, currentVersion, onNavigate, onRestore, disabled }: {
  versions: VersionEntry[];
  currentVersion: number;
  onNavigate: (version: number) => void;
  onRestore: (version: number) => void;
  disabled: boolean;
}) {
  if (versions.length <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 overflow-x-auto border-t border-[var(--color-border)] bg-[var(--color-surface)] p-2">
      <span className="mr-2 whitespace-nowrap text-xs text-[var(--color-muted-foreground)]">Checkpoint history:</span>
      {versions.map((version) => (
        <button
          key={version.versionNumber}
          onClick={() => onNavigate(version.versionNumber)}
          onDoubleClick={() => onRestore(version.versionNumber)}
          disabled={disabled}
          className={`whitespace-nowrap rounded px-2 py-1 text-xs ${
            version.versionNumber === currentVersion
              ? "bg-[var(--color-primary)] text-white"
              : "border border-[var(--color-border)] bg-background text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
          } disabled:cursor-not-allowed disabled:opacity-50`}
          title={`${version.description}\nDouble-click to restore this version`}
        >
          v{version.versionNumber}
        </button>
      ))}
    </div>
  );
}

export default function ConversationalBuilder() {
  const navigate = useNavigate();
  const { mutate: createCheckpoint, error: checkpointError } = useAiPageGeneration();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [checkpoint, setCheckpoint] = useState<AiCheckpointDetail | null>(null);
  const [pageState, setPageState] = useState<PuckData>({ content: [] });
  const [pageType, setPageType] = useState("lead_capture");
  const [displayVersion, setDisplayVersion] = useState<number | null>(null);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const syncCheckpoint = useCallback(async (checkpointId: string) => {
    const detail = await getAiCheckpoint(checkpointId);
    setCheckpoint(detail);
    setDisplayVersion(detail.currentVersion);
    return detail;
  }, []);

  const visibleVersion = useMemo(() => {
    if (!checkpoint) {
      return null;
    }

    const versionNumber = displayVersion ?? checkpoint.currentVersion;
    return checkpoint.versions.find((version) => version.versionNumber === versionNumber) ?? null;
  }, [checkpoint, displayVersion]);

  useEffect(() => {
    const puckData = visibleVersion?.output.puckData ?? null;
    setPageState(puckData ?? { content: [] });
  }, [visibleVersion]);

  useEffect(() => {
    const content = visibleVersion?.output.puckData?.content ?? [];
    if (!selectedComponentId || content.some((component, index) => getComponentId(component, index) === selectedComponentId)) {
      return;
    }

    setSelectedComponentId(null);
  }, [selectedComponentId, visibleVersion]);

  useEffect(() => {
    if (!checkpoint || title.trim().length > 0) {
      return;
    }

    const nextTitle = buildDraftTitle(pageType);
    setTitle(nextTitle);
    setSlug(slugify(nextTitle));
  }, [checkpoint, pageType, title]);

  const versionEntries = useMemo<VersionEntry[]>(() => {
    if (!checkpoint) {
      return [];
    }

    return checkpoint.versions.map((version) => ({
      versionNumber: version.versionNumber,
      description: version.promptUsed || `Version ${version.versionNumber}`,
      timestamp: Date.parse(version.createdAt),
    }));
  }, [checkpoint]);

  const currentDiffs = useMemo(() => {
    return visibleVersion?.generationMetadata?.conversationalDiffs ?? [];
  }, [visibleVersion]);

  const appendAssistantMessage = useCallback((content: string) => {
    setMessages((previous) => [
      ...previous,
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content,
        timestamp: Date.now(),
      },
    ]);
  }, []);

  const handleSendMessage = useCallback(async (text: string) => {
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      timestamp: Date.now(),
    };
    setMessages((previous) => [...previous, userMessage]);
    setIsWorking(true);
    setSurfaceError(null);
    setStatusMessage(null);

    try {
      if (!checkpoint) {
        const createdCheckpoint = await createCheckpoint({ prompt: text, pageType });
        if (!createdCheckpoint?.id) {
          throw new Error(checkpointError ?? "Checkpoint creation did not return an id");
        }

        await syncCheckpoint(createdCheckpoint.id);
        const nextTitle = buildDraftTitle(pageType);
        setTitle((current) => current || nextTitle);
        setSlug((current) => current || slugify(nextTitle));
        setStatusMessage("Draft checkpoint created. Review the preview, keep iterating, then approve it into the editor.");
        appendAssistantMessage("Created a real checkpoint draft. You can keep chatting to refine it, restore any previous version, or approve it into the editor.");
        return;
      }

      await modifyAiCheckpoint(checkpoint.id, text, { selectedComponentId });
      await syncCheckpoint(checkpoint.id);
      setStatusMessage("Checkpoint refined. The preview now reflects the latest version.");
      appendAssistantMessage("Applied your instruction as concrete page diffs and added a new checkpoint version. Review the diff chips, then keep iterating or approve.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update the checkpoint.";
      setSurfaceError(message);
      appendAssistantMessage("I could not update the checkpoint. Review the error and try again.");
    } finally {
      setIsWorking(false);
    }
  }, [appendAssistantMessage, checkpoint, checkpointError, createCheckpoint, pageType, selectedComponentId, syncCheckpoint]);

  const handleRestore = useCallback(async (versionNumber: number) => {
    if (!checkpoint) {
      return;
    }

    setIsWorking(true);
    setSurfaceError(null);

    try {
      await revertAiCheckpoint(checkpoint.id, versionNumber);
      await syncCheckpoint(checkpoint.id);
      setStatusMessage(`Restored checkpoint version v${versionNumber} as the current draft.`);
    } catch (error) {
      setSurfaceError(error instanceof Error ? error.message : "Failed to restore checkpoint version.");
    } finally {
      setIsWorking(false);
    }
  }, [checkpoint, syncCheckpoint]);

  const handleApprove = useCallback(async () => {
    if (!checkpoint || !title.trim() || !slug.trim()) {
      return;
    }

    setIsApproving(true);
    setSurfaceError(null);

    try {
      const approved = await approveAiCheckpoint(checkpoint.id, {
        title: title.trim(),
        slug: slug.trim(),
        pageType,
      });
      navigate(`/pages/editor/${approved.page.id}`);
    } catch (error) {
      setSurfaceError(error instanceof Error ? error.message : "Failed to approve checkpoint.");
    } finally {
      setIsApproving(false);
    }
  }, [checkpoint, navigate, pageType, slug, title]);

  const handleReject = useCallback(async () => {
    if (!checkpoint) {
      return;
    }

    setIsRejecting(true);
    setSurfaceError(null);

    try {
      await rejectAiCheckpoint(checkpoint.id);
      setCheckpoint(null);
      setDisplayVersion(null);
      setPageState({ content: [] });
      setStatusMessage("Checkpoint rejected. No draft was committed.");
    } catch (error) {
      setSurfaceError(error instanceof Error ? error.message : "Failed to reject checkpoint.");
    } finally {
      setIsRejecting(false);
    }
  }, [checkpoint]);

  return (
    <div className="flex h-full">
      <div className="w-[360px] shrink-0">
        <ChatPanel messages={messages} onSend={handleSendMessage} isLoading={isWorking} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="grid gap-4 border-b border-[var(--color-border)] bg-background p-4 lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)_auto]">
          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Page type</span>
            <select
              value={pageType}
              onChange={(event) => setPageType(event.target.value)}
              disabled={Boolean(checkpoint) || isWorking}
              className="w-full rounded-lg border border-[var(--color-border)] bg-background px-3 py-2"
            >
              <option value="lead_capture">Lead capture</option>
              <option value="site_page">Site page</option>
              <option value="sales">Sales</option>
              <option value="booking">Booking</option>
              <option value="webinar">Webinar</option>
              <option value="thank_you">Thank you</option>
            </select>
          </label>

          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Draft title</span>
            <input
              value={title}
              onChange={(event) => {
                const nextTitle = event.target.value;
                setTitle(nextTitle);
                setSlug((current) => current || slugify(nextTitle));
              }}
              placeholder="AI Draft"
              className="w-full rounded-lg border border-[var(--color-border)] bg-background px-3 py-2"
            />
          </label>

          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Slug</span>
            <input
              value={slug}
              onChange={(event) => setSlug(slugify(event.target.value))}
              placeholder="ai-draft"
              className="w-full rounded-lg border border-[var(--color-border)] bg-background px-3 py-2"
            />
          </label>

          <div className="flex items-end gap-2">
            <button
              onClick={handleReject}
              disabled={!checkpoint || isRejecting || isWorking || isApproving}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium hover:bg-[var(--color-muted)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRejecting ? "Rejecting…" : "Reject"}
            </button>
            <button
              onClick={handleApprove}
              disabled={!checkpoint || !title.trim() || !slug.trim() || isApproving || isWorking || isRejecting}
              className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isApproving ? "Approving…" : "Approve Draft"}
            </button>
          </div>
        </div>

        {(surfaceError || statusMessage) ? (
          <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm">
            {surfaceError ? (
              <p className="text-[var(--color-destructive)]">{surfaceError}</p>
            ) : null}
            {statusMessage ? (
              <p className="text-[var(--color-muted-foreground)]">{statusMessage}</p>
            ) : null}
          </div>
        ) : null}

        <DiffPreviewPanel diffs={currentDiffs} selectedComponentId={selectedComponentId} />

        <div className="min-h-0 flex-1">
          <PreviewPanel
            state={pageState}
            isLoading={isWorking}
            versionNumber={visibleVersion?.versionNumber ?? null}
            selectedComponentId={selectedComponentId}
            onSelectComponent={setSelectedComponentId}
          />
        </div>

        <VersionBar
          versions={versionEntries}
          currentVersion={displayVersion ?? checkpoint?.currentVersion ?? 0}
          onNavigate={setDisplayVersion}
          onRestore={handleRestore}
          disabled={isWorking || isApproving || isRejecting}
        />
      </div>
    </div>
  );
}
